import { motion, useReducedMotion } from "framer-motion";
import { BookOpen, Globe2, Plane, GraduationCap, MessageCircle, Sparkles, Rocket } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const containerReveal = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const item = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

const FloatingIcon = ({ Icon, className, delay = 0, size = 28, color = "#D90429", bg = "#fff" }) => (
  <motion.div
    className={`absolute grid place-items-center rounded-2xl shadow-xl ${className}`}
    style={{ background: bg, color, width: size + 24, height: size + 24 }}
    animate={{ y: [0, -18, 0], rotate: [0, 6, 0] }}
    transition={{ duration: 6 + delay, repeat: Infinity, ease: "easeInOut", delay }}
  >
    <Icon style={{ width: size, height: size }} />
  </motion.div>
);

const Shape3D = ({ className, delay = 0, gradient }) => (
  <motion.div
    className={`absolute rounded-3xl ${className}`}
    style={{ background: gradient, filter: "blur(0.5px)" }}
    animate={{ y: [0, -30, 0], rotate: [0, 15, 0], scale: [1, 1.05, 1] }}
    transition={{ duration: 9 + delay, repeat: Infinity, ease: "easeInOut", delay }}
  />
);

export const AnimatedHero = () => {
  const reduced = useReducedMotion();
  const { user } = useAuth();

  return (
    <section className="relative overflow-hidden pt-16 pb-24 md:pt-24 md:pb-32">
      {/* animated blob background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-brand-red/20 blur-3xl animate-blob" />
        <div className="absolute -bottom-40 -right-20 w-[520px] h-[520px] rounded-full bg-brand-gold/25 blur-3xl animate-blob" style={{ animationDelay: "4s" }} />
        <div className="absolute top-1/3 left-1/2 w-[380px] h-[380px] rounded-full bg-brand-navy/10 blur-3xl animate-blob" style={{ animationDelay: "8s" }} />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 25%, #14213D 1px, transparent 1px), radial-gradient(circle at 75% 75%, #D90429 1px, transparent 1px)",
            backgroundSize: "48px 48px, 64px 64px",
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center relative">
        <motion.div variants={containerReveal} initial="hidden" animate="show" className="lg:col-span-7 relative z-10">
          <motion.div variants={item} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-brand-red/20 text-brand-red text-xs font-semibold uppercase tracking-widest shadow-sm">
            #Speak, Study, Succeed
            <span aria-hidden="true">⭐</span>
          </motion.div>

          <motion.h1
            variants={item}
            className="mt-6 font-heading font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.98] text-brand-navy text-balance"
          >
            Where Indian ambition <br className="hidden md:block" />
            meets <span className="relative inline-block">
              <span className="relative z-10 text-brand-red">global fluency.</span>
              <motion.svg
                className="absolute -bottom-3 left-0 w-full"
                viewBox="0 0 300 12"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 1.1, duration: 1.2, ease: "easeInOut" }}
              >
                <motion.path d="M2 8 Q 80 -2 150 6 T 298 6" stroke="#FFC300" strokeWidth="5" fill="none" strokeLinecap="round" />
              </motion.svg>
            </span>
          </motion.h1>

          <motion.p variants={item} className="mt-6 text-lg text-brand-navy/70 max-w-xl leading-relaxed">
            IELTS · PTE · Spoken English · Interview Prep · Study Abroad. Small batches, seasoned mentors,
            and a proven playbook that has helped 5,000+ students land the score, the offer, and the visa.
          </motion.p>

          <motion.div variants={item} className="mt-9 flex flex-col sm:flex-row gap-3">
            <Link to="/register" data-testid="hero-primary-cta">
              <Button className="rounded-full px-8 h-14 text-base font-semibold bg-brand-red hover:bg-brand-red-hover text-white brand-shadow btn-scale">
                Book a Free Demo
              </Button>
            </Link>
            {user && (
              <a
                href="https://practice.languagenestindia.com/#/"
                target="_blank"
                rel="noreferrer"
                data-testid="hero-start-practicing-btn"
              >
                <Button className="rounded-full px-8 h-14 text-base font-semibold bg-brand-red hover:bg-brand-red-hover text-white brand-shadow btn-scale inline-flex items-center gap-2">
                  <Rocket className="w-4 h-4" />
                  Start Practicing
                </Button>
              </a>
            )}
            <Link to="/coaching" data-testid="hero-secondary-cta">
              <Button variant="outline" className="rounded-full px-8 h-14 text-base font-semibold border-2 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white btn-scale">
                Explore Courses
              </Button>
            </Link>
          </motion.div>

          <motion.div variants={item} className="mt-10 grid grid-cols-3 gap-5 max-w-md">
            {[
              { n: "5k+", l: "Students trained" },
              { n: "8.0", l: "Avg IELTS band" },
              { n: "27+", l: "Countries placed" },
            ].map((s) => (
              <div key={s.l} className="rounded-2xl bg-white/80 border border-brand-navy/10 px-4 py-3 backdrop-blur-md">
                <div className="font-heading font-black text-2xl text-brand-red">{s.n}</div>
                <div className="text-xs text-brand-navy/60 leading-tight mt-1">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Right: floating 3D scene */}
        <div className="lg:col-span-5 relative h-[440px] sm:h-[520px] lg:h-[560px]">
          {!reduced && (
            <>
              {/* Big red square with PTE 75+ score */}
              <motion.div
                className="absolute w-44 h-44 sm:w-52 sm:h-52 top-6 right-8 rounded-3xl grid place-items-center text-white text-center shadow-[0_25px_60px_-15px_rgba(217,4,41,0.55)]"
                style={{ background: "linear-gradient(135deg,#D90429,#FF4D6D)" }}
                animate={{ y: [0, -30, 0], rotate: [0, 8, 0], scale: [1, 1.03, 1] }}
                transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
                data-testid="hero-pte-badge"
              >
                <div>
                  <div className="font-heading font-black text-4xl sm:text-5xl leading-none">PTE 75+</div>
                  <div className="mt-1 text-sm sm:text-base font-medium tracking-wide opacity-95">score</div>
                </div>
              </motion.div>

              {/* Stacked navy squares with airplane */}
              <motion.div
                className="absolute w-40 h-40 sm:w-48 sm:h-48 top-1/2 left-4 rounded-3xl -translate-y-1/2 rotate-6 shadow-[0_25px_60px_-15px_rgba(20,33,61,0.4)]"
                style={{ background: "linear-gradient(135deg,#1B2B48,#0F1B33)" }}
                animate={{ y: [0, -20, 0], rotate: [6, 10, 6] }}
                transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
                aria-hidden
              />
              <motion.div
                className="absolute w-40 h-40 sm:w-48 sm:h-48 top-1/2 left-2 rounded-3xl -translate-y-[42%] -rotate-6 grid place-items-center text-white shadow-[0_25px_60px_-15px_rgba(20,33,61,0.55)]"
                style={{ background: "linear-gradient(135deg,#14213D,#3D5A80)" }}
                animate={{ y: [0, -18, 0], rotate: [-6, -2, -6] }}
                transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
                data-testid="hero-abroad-badge"
              >
                <Plane className="w-16 h-16 sm:w-20 sm:h-20" strokeWidth={1.6} />
              </motion.div>

              {/* Yellow accent orb */}
              <Shape3D
                className="w-24 h-24 bottom-16 left-4 rounded-full"
                gradient="linear-gradient(135deg,#FFC300,#FFD60A)"
                delay={2}
              />

              <FloatingIcon Icon={BookOpen} className="top-10 left-4" delay={0} color="#D90429" />
              <FloatingIcon Icon={GraduationCap} className="bottom-24 left-16" delay={0.8} color="#fff" bg="#D90429" />
              <FloatingIcon Icon={MessageCircle} className="top-6 right-1/3" delay={3} color="#D90429" />
              <FloatingIcon Icon={Globe2} className="top-32 right-2" delay={1} color="#14213D" size={22} />
            </>
          )}

          {/* Center 3D card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85, rotate: -6 }}
            animate={{ opacity: 1, scale: 1, rotate: -4 }}
            transition={{ delay: 0.4, duration: 1, ease: "easeOut" }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 sm:w-72"
          >
            <motion.div
              animate={{ y: [0, -12, 0], rotate: [-4, -2, -4] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="rounded-3xl bg-white p-6 border border-brand-navy/10 shadow-[0_30px_80px_-20px_rgba(217,4,41,0.35)]"
            >
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-red">
                <div className="w-2 h-2 rounded-full bg-brand-red animate-pulse" /> Live batch
              </div>
              <div className="mt-3 font-heading font-black text-2xl text-brand-navy leading-tight">IELTS Fast-Track</div>
              <div className="text-sm text-brand-navy/60 mt-1">25-Days Intensive · Band 7+</div>
              <div className="mt-5 flex items-end justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-brand-navy/50">Starts</div>
                  <div className="font-semibold text-brand-navy">Every Monday</div>
                </div>
                <div className="rounded-full bg-brand-gold text-brand-navy font-black text-sm px-3 py-1.5">
                  ₹5499
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
